// src/pages/ClubProjects.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import usePageTitle from "../hooks/usePageTitle";
import usePolling from "../hooks/usePolling";
import { Link } from "react-router-dom";
import {
  FaPlus,
  FaSearch,
  FaCheckCircle,
  FaHourglassHalf,
  FaRocket,
  FaExclamationTriangle,
  FaChevronDown,
  FaThumbsUp,
  FaClock,
  FaStar,
  FaTimesCircle,
  FaShieldAlt,
  FaFolderOpen,
} from "react-icons/fa";
import { brand, shadow } from "../styles/theme";

/* ------------------------------- Constants ------------------------------- */

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

// Layout offsets — match desktop Sidebar/Topbar chrome (unchanged geometry).
// This page's outer wrapper is `display:flex` (row), so Sidebar/Topbar/main
// sit side-by-side as flex items -- Topbar's own in-flow spacer never pushes
// main down in that context (only a vertical stack would work that way).
// main needs its own explicit top offset regardless of what Topbar does.
const SIDEBAR_W = 256; // px
const MAIN_GUTTER = 40; // px
const TOPBAR_H = 56; // px

/* ------------------------------ Style System ------------------------------
   Local tokens now source from the shared brand palette (src/styles/theme.js)
   and the .leo-shell-v2 CSS custom properties (defined on the page's ancestor
   wrapper, see App.js) instead of one-off hex values, so this page reads as
   part of the same design system as the rest of the app.
----------------------------------------------------------------------------*/
const C = {
  ink: brand.text,
  mute: brand.textMuted,
  meta: brand.textFaint,
  border: brand.border,
  borderStrong: brand.borderStrong,

  accent: brand.accent, // purple — the one dominant interactive accent
  accentSoft: brand.accentSoft,
  accentDark: brand.accentDark, // dark red — "needs attention" only
  accentDarkSoft: brand.accentDarkSoft,
  blue: brand.blue, // secondary / info accent
  blueSoft: brand.blueSoft,

  success: brand.success,
  successSoft: brand.successSoft,
  warning: brand.warning,
  warningSoft: brand.warningSoft,
  danger: brand.danger,
  dangerSoft: brand.dangerSoft,

  shadow: shadow.raised,
};

const S = {
  font: { fontFamily: '"Inter","SF Pro Text",system-ui,-apple-system,Segoe UI,Roboto,sans-serif' },
  app: { display: "flex", minHeight: "100vh", background: "var(--bg-base)", color: "var(--text-1)" },

  main: (isDesktop) => ({
    width: "100%",
    maxWidth: 1320,
    margin: "0 auto",
    padding: "24px 24px 48px",
    paddingTop: TOPBAR_H,
    paddingLeft: isDesktop ? SIDEBAR_W + MAIN_GUTTER : 16,
    boxSizing: "border-box",
    position: "relative",
    minWidth: 0,
  }),
};

/* ------------------------------ Utilities -------------------------------- */

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
}

const toJsonSafe = async (res) => {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    console.error("Non-JSON response:", text.slice(0, 300));
    throw new Error("Invalid JSON from server");
  }
};

const toneKey = (s) => {
  const k = String(s || "new").toLowerCase();
  if (k === "approved") return "approved";
  if (k === "pending") return "pending";
  if (k === "rejected") return "rejected";
  return "new";
};

const shortDate = (s) => (s ? String(s).split(" ")[0] : "");

// status -> shared .pill modifier + label (real status pills, not colored text)
const PILL_CLASS = { approved: "pill-success", pending: "pill-warning", new: "pill-info", rejected: "pill-danger" };
const STATUS_LABEL = { approved: "Approved", pending: "Pending", new: "New", rejected: "Rejected" };

// status tabs shown as filter chips (mirrors the sections the page has
// always grouped projects into — rejected projects have never been
// surfaced in this view, and that stays true here)
const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "new", label: "New" },
  { key: "approved", label: "Approved" },
];

const EMPTY_COPY = {
  all: { icon: FaFolderOpen, text: "No projects to show yet.", hint: "Create the first proposal to get things moving." },
  pending: { icon: FaHourglassHalf, text: "Nothing pending review.", hint: "Submitted proposals waiting on a decision will land here." },
  new: { icon: FaRocket, text: "No new drafts yet.", hint: "Freshly created proposals will show up in this tab." },
  approved: { icon: FaCheckCircle, text: "No approved projects yet.", hint: "Approved proposals will show up in this tab." },
};

/* -------------------------------- Component ------------------------------- */

export default function ClubProjects() {
  // desktop check
  const useDesktop = () => {
    const [isDesktop, set] = React.useState(() => window.matchMedia("(min-width: 1024px)").matches);
    React.useEffect(() => {
      const mq = window.matchMedia("(min-width: 1024px)");
      const on = (e) => set(e.matches);
      mq.addEventListener?.("change", on) || mq.addListener(on);
      return () => mq.removeEventListener?.("change", on) || mq.removeListener(on);
    }, []);
    return isDesktop;
  };
  const isDesktop = useDesktop();

  /* ------------------------------ User/Admin ------------------------------ */
  const rawUser = useMemo(() => readUser(), []);
  const user = rawUser?.name ? rawUser : { name: "Unknown User", role: "Member", image: "/user.png" };
  const isAdmin =
    String(user?.role || "").toLowerCase().includes("admin") ||
    user?.is_admin === 1 ||
    user?.isAdmin === 1;

  /* --------------------------------- State -------------------------------- */
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("created_desc");
  const [statusFilter, setStatusFilter] = useState("all"); // presentation-only filter chip

  // Cache of { [SLIITID]: name }
  const [memberNames, setMemberNames] = useState({});
  const [namesLoading, setNamesLoading] = useState(false);

  /* --------------------------------- Load --------------------------------- */
  // `silent` skips the loading-spinner flash -- used by the background
  // poll below so new/updated projects appear on their own without the
  // whole list blinking back to a skeleton state every refresh.
  const loadProjects = useCallback(async (silent = false) => {
    try {
      setErr("");
      if (!silent) setLoading(true);
      let data;

      if (isAdmin) {
        try {
          const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_all.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          });
          if (!res.ok) throw new Error();
          const d = await toJsonSafe(res);
          if (d?.success && Array.isArray(d.projects)) data = d;
        } catch {}

        if (!data) {
          try {
            const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=list_all`);
            if (res.ok) {
              const d = await toJsonSafe(res);
              if (d?.success && Array.isArray(d.projects)) data = d;
            }
          } catch {}
        }

        if (!data) {
          const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_visible.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ SLIITID: user?.SLIITID || null }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          data = await toJsonSafe(res);
        }
      } else {
        try {
          const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_visible.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ SLIITID: user?.SLIITID || null }),
          });
          if (!res.ok) throw new Error();
          data = await toJsonSafe(res);
        } catch {
          const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_by_user.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ SLIITID: user?.SLIITID || null }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          data = await toJsonSafe(res);
        }
      }

      setProjects(Array.isArray(data?.projects) ? data.projects : []);
    } catch {
      setErr("Failed to load projects");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user?.SLIITID]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // Live-refresh the project list in the background so a newly-created or
  // updated project shows up on its own, no manual reload needed.
  usePolling(() => loadProjects(true), { intervalMs: 20000, hiddenIntervalMs: 60000 });

 /* -------------------------- Fetch Member Names --------------------------- */
useEffect(() => {
  // unique, trimmed ids from projects
  const ids = Array.from(
    new Set(
      projects
        .map(p => String(p?.created_by_sliitid ?? '').trim())
        .filter(Boolean)
    )
  ).filter(id => !(id in memberNames));

  if (!ids.length) return;

  const abort = new AbortController();
  const { signal } = abort;

  (async () => {
    setNamesLoading(true);
    try {
      // Split into numeric SLIITLeoID vs string SLIITID
      const numericIds = ids.filter(id => /^\d+$/.test(id)).map(id => Number(id));
      const stringIds  = ids.filter(id => !/^\d+$/.test(id)); // likely ITxxxxxxx

      const merged = {};

      // 1) Bulk fetch by numeric SLIITLeoID (only if we actually have numeric ids)
      if (numericIds.length) {
        try {
          const bulkRes = await fetch(
            `${ABSOLUTE_HOST}/Backend/memberbasic_api.php?action=names_by_ids`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ids: numericIds }),
              signal,
            }
          );

          if (bulkRes.ok) {
            const bulkData = await bulkRes.json();
            // backend returns: { success: true, data: [{ SLIITLeoID, FullName }, ...] }
            if (bulkData?.success && Array.isArray(bulkData.data)) {
              for (const row of bulkData.data) {
                const key = String(row?.SLIITLeoID ?? '').trim();
                const name = String(row?.FullName ?? '').trim();
                if (key && name) merged[key] = name;
              }
            }
          }
        } catch {
          // ignore bulk errors; we'll still try strings below
        }
      }

      // 2) Fetch by SLIITID (string) individually (concurrent, safe)
      if (stringIds.length) {
        const lookups = await Promise.allSettled(
          stringIds.map(async (id) => {
            const r = await fetch(
              `${ABSOLUTE_HOST}/Backend/memberbasic_api.php?action=get_by_sliitid`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // IMPORTANT: backend expects "sliitid" (lowercase)
                body: JSON.stringify({ sliitid: id }),
                signal,
              }
            );
            if (!r.ok) return [id, null];
            const d = await r.json();
            // backend returns: { success: true, data: { FullName, SLIITID, ... } } or null
            const name =
              d?.data?.FullName ??
              d?.FullName ??
              d?.data?.name ??
              d?.name ??
              '';
            return [id, name ? String(name).trim() : null];
          })
        );

        for (const res of lookups) {
          if (res.status === 'fulfilled') {
            const [id, name] = res.value;
            if (id && name) merged[id] = name;
          }
        }
      }

      // 3) Commit any new names
      if (Object.keys(merged).length) {
        setMemberNames(cur => ({ ...cur, ...merged }));
      }
    } catch {
      // ignore (network/abort)
    } finally {
      if (!signal.aborted) setNamesLoading(false);
    }
  })();

  return () => abort.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [projects, memberNames]);


  /* ------------------------------ Name Resolve ----------------------------- */
  const creatorName = (p) => {
    if (p?.created_by_name && String(p.created_by_name).trim()) return p.created_by_name;
    const id = String(p?.created_by_sliitid || "").trim();
    if (id && memberNames[id]) return memberNames[id];
    return (
      p?.created_by ||
      p?.createdUser ||
      p?.created_user ||
      p?.creator ||
      p?.owner ||
      p?.proposed_by ||
      p?.submitted_by ||
      (id ? id : "Unknown")
    );
  };

  /* ------------------------------ Transform -------------------------------- */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...projects];

    if (q) {
      list = list.filter((p) => {
        const nameHit = String(p.name || "").toLowerCase().includes(q);
        const creatorHit = String(creatorName(p) || "").toLowerCase().includes(q);
        return nameHit || creatorHit;
      });
    }

    if (sortKey === "created_desc") {
      list.sort((a, b) => {
        const ad = new Date(a.created_at || 0).getTime();
        const bd = new Date(b.created_at || 0).getTime();
        return bd - ad;
      });
    } else if (sortKey === "name_asc") {
      list.sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, query, sortKey, memberNames]);

  const pending  = useMemo(() => filtered.filter((p) => toneKey(p.status) === "pending"),  [filtered]);
  const fresh    = useMemo(() => filtered.filter((p) => toneKey(p.status) === "new"),      [filtered]);
  const approved = useMemo(() => filtered.filter((p) => toneKey(p.status) === "approved"), [filtered]);

  // The single visible dataset — same set the old tile sections rendered
  // (Pending + New + Approved). Rejected projects have never been shown in
  // this view; that behavior is unchanged, just no longer split into three
  // separate stacked sections.
  const visibleAll = useMemo(
    () => filtered.filter((p) => toneKey(p.status) !== "rejected"),
    [filtered]
  );

  const displayed = useMemo(() => {
    if (statusFilter === "all") return visibleAll;
    return visibleAll.filter((p) => toneKey(p.status) === statusFilter);
  }, [visibleAll, statusFilter]);

  /* ---------------------------- Status Update ------------------------------ */
  const updateStatus = useCallback(
    async (project, next) => {
      if (!isAdmin) return;
      const id = Number(project.id);
      if (!id) return;

      const prev = [...projects];
      setProjects((cur) => cur.map((p) => (p.id === id ? { ...p, status: next } : p)));

      try {
        const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=update_status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id,
            status: next,
            approved_by: user?.name || "Admin",
          }),
        });
        const d = await toJsonSafe(res);
        if (!d?.success) {
          alert(d?.message || "Status update failed");
          setProjects(prev);
        }
      } catch {
        alert("Status update failed");
        setProjects(prev);
      }
    },
    [isAdmin, projects, user?.name]
  );

  /* ----------------------------------- UI ---------------------------------- */

  const StatusPill = ({ status }) => {
    const k = toneKey(status);
    return (
      <span className={`pill ${PILL_CLASS[k]}`}>
        <span className="dot" />
        {STATUS_LABEL[k]}
      </span>
    );
  };

  // Admin-only status changer — same updateStatus() call as before, restyled
  // to match the shared button/panel system.
  const StatusDropdown = ({ current, onSelect }) => {
    const [open, setOpen] = useState(false);
    const ddRef = useRef(null);
    const items = [
      { value: "approved", label: "Approve", icon: <FaThumbsUp /> },
      { value: "pending", label: "Mark Pending", icon: <FaClock /> },
      { value: "new", label: "Mark New", icon: <FaStar /> },
      { value: "rejected", label: "Reject", icon: <FaTimesCircle /> },
    ];

    useEffect(() => {
      if (!open) return;
      const onDocClick = (e) => {
        if (ddRef.current && !ddRef.current.contains(e.target)) setOpen(false);
      };
      const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
      document.addEventListener("mousedown", onDocClick);
      document.addEventListener("keydown", onEsc);
      return () => {
        document.removeEventListener("mousedown", onDocClick);
        document.removeEventListener("keydown", onEsc);
      };
    }, [open]);

    return (
      <div className="cp-dd" ref={ddRef}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          Manage <FaChevronDown style={{ fontSize: 10, opacity: 0.85 }} />
        </button>

        {open && (
          <div className="cp-dd-menu panel" role="menu">
            {items.map((it) => (
              <button
                key={it.value}
                type="button"
                role="menuitem"
                className={`cp-dd-item${it.value === current ? " active" : ""}`}
                onClick={() => { onSelect(it.value); setOpen(false); }}
              >
                <span className="cp-dd-icon">{it.icon}</span>
                <span>{it.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const EmptyState = ({ tab }) => {
    const copy = EMPTY_COPY[tab] || EMPTY_COPY.all;
    const Icon = copy.icon;
    return (
      <div className="empty-state">
        <span className="empty-icon"><Icon /></span>
        <h3>{copy.text}</h3>
        <p>{copy.hint}</p>
      </div>
    );
  };

  const SkeletonRow = () => (
    <tr aria-hidden>
      <td>
        <div className="skeleton" style={{ height: 12, width: 190, borderRadius: 6, marginBottom: 8 }} />
        <div className="skeleton" style={{ height: 10, width: 120, borderRadius: 6 }} />
      </td>
      <td><div className="skeleton" style={{ height: 20, width: 78, borderRadius: 999 }} /></td>
      <td><div className="skeleton" style={{ height: 10, width: 66, borderRadius: 6 }} /></td>
      <td><div className="skeleton" style={{ height: 10, width: 90, borderRadius: 6 }} /></td>
      <td style={{ textAlign: "right" }}>
        <div className="skeleton" style={{ height: 26, width: 84, borderRadius: 8, marginLeft: "auto" }} />
      </td>
    </tr>
  );

  const SkeletonCard = () => (
    <div className="panel cp-mobile-card" aria-hidden>
      <div className="skeleton" style={{ height: 13, width: 160, borderRadius: 6, marginBottom: 8 }} />
      <div className="skeleton" style={{ height: 10, width: 110, borderRadius: 6, marginBottom: 10 }} />
      <div className="skeleton" style={{ height: 26, width: "70%", borderRadius: 8 }} />
    </div>
  );

  const ProjectRow = ({ project }) => {
    const k = toneKey(project.status);
    return (
      <tr>
        <td style={{ maxWidth: 0, width: "42%" }}>
          <div className="proj-name truncate" style={{ maxWidth: 360 }} title={project.name}>{project.name || "Untitled project"}</div>
          <div className="proj-cat truncate" style={{ maxWidth: 360 }}>by {creatorName(project)}</div>
        </td>
        <td><StatusPill status={project.status} /></td>
        <td className="num" style={{ color: "var(--text-2)" }}>{shortDate(project.created_at) || "—"}</td>
        <td style={{ color: "var(--text-3)", fontSize: 12.5, maxWidth: 160 }} className="truncate">
          {project.approved_by || "—"}
        </td>
        <td className={isAdmin ? "cp-admin-cell" : undefined}>
          <div className="row-actions">
            <Link to={`/project/${project.id}`} className="btn btn-ghost btn-sm">Open</Link>
            {isAdmin && <StatusDropdown current={k} onSelect={(s) => updateStatus(project, s)} />}
          </div>
        </td>
      </tr>
    );
  };

  const ProjectCardRow = ({ project }) => {
    const k = toneKey(project.status);
    return (
      <div className="panel cp-mobile-card">
        <div className="cp-mobile-top">
          <div style={{ minWidth: 0 }}>
            <div className="proj-name truncate" title={project.name}>{project.name || "Untitled project"}</div>
            <div className="proj-cat truncate">by {creatorName(project)}</div>
          </div>
          <StatusPill status={project.status} />
        </div>

        <div className="cp-mobile-meta">
          <span className="num">{shortDate(project.created_at) || "—"}</span>
          {project.approved_by && <span className="truncate">approved by {project.approved_by}</span>}
        </div>

        <div className="cp-mobile-actions">
          <Link to={`/project/${project.id}`} className="btn btn-ghost btn-sm">Open</Link>
          {isAdmin && <StatusDropdown current={k} onSelect={(s) => updateStatus(project, s)} />}
        </div>
      </div>
    );
  };

  const isBusy = loading || namesLoading;

  /* --------------------------------- Render -------------------------------- */
  return (
    <div style={{ ...S.app, ...S.font }}>
      <Sidebar />
      <Topbar user={user} />

      <main style={S.main(isDesktop)}>
        {/* Header */}
        <div className="cp-header">
          <div>
            <div className="eyebrow">Projects</div>
            <h1 className="cp-title">Club Projects</h1>
            <p className="page-desc">Search, filter, and manage every proposal in one place.</p>
          </div>

          <div className="cp-header-right">
            {isAdmin && (
              <span className="cp-admin-pill" title="You are viewing admin-wide data across all members">
                <FaShieldAlt /> Admin Mode
              </span>
            )}
            <span className="pill pill-info"><span className="dot" />{filtered.length} total</span>
          </div>
        </div>

        {/* Toolbar: status chips + search + sort + New */}
        <div className="cp-toolbar">
          <div className="cp-chip-row" role="tablist" aria-label="Filter projects by status">
            {STATUS_TABS.map((t) => {
              const count =
                t.key === "all" ? visibleAll.length :
                t.key === "pending" ? pending.length :
                t.key === "new" ? fresh.length : approved.length;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === t.key}
                  className={`chip${statusFilter === t.key ? " active" : ""}`}
                  onClick={() => setStatusFilter(t.key)}
                >
                  {t.label} <span className="num" style={{ opacity: 0.75, marginLeft: 4 }}>{count}</span>
                </button>
              );
            })}
          </div>

          <div className="cp-tools">
            <div className="cp-search">
              <FaSearch className="cp-search-icon" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by project or creator…"
              />
            </div>

            <div className="cp-select-wrap">
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                <option value="created_desc">Newest first</option>
                <option value="name_asc">Name A → Z</option>
              </select>
              <FaChevronDown className="cp-select-icon" />
            </div>

            <Link to="/create-project" className="btn btn-primary">
              <FaPlus /> New
            </Link>
          </div>
        </div>

        {/* Error banner */}
        {err && (
          <div className="error-banner" style={{ marginBottom: 16 }}>
            <FaExclamationTriangle /> <span>{err}</span>
          </div>
        )}

        {/* Project list */}
        <div className="panel cp-panel">
          {isDesktop ? (
            <div className="cp-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Approved By</th>
                    <th className={isAdmin ? "cp-admin-cell" : undefined} style={{ textAlign: "right" }}>
                      {isAdmin ? "Admin Actions" : "Open"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isBusy ? (
                    Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
                  ) : displayed.length === 0 ? (
                    <tr>
                      <td colSpan={5}><EmptyState tab={statusFilter} /></td>
                    </tr>
                  ) : (
                    displayed.map((p) => <ProjectRow key={p.id} project={p} />)
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="cp-cards">
              {isBusy ? (
                Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
              ) : displayed.length === 0 ? (
                <EmptyState tab={statusFilter} />
              ) : (
                displayed.map((p) => <ProjectCardRow key={p.id} project={p} />)
              )}
            </div>
          )}
        </div>
      </main>

      {/* Page-scoped styles: layered on top of the shared .leo-shell-v2 system
          (shell.css) — small layout bits specific to this page only. */}
      <style>{`
        .cp-header { display:flex; flex-wrap:wrap; align-items:flex-end; justify-content:space-between; gap:16px; margin-bottom:20px; }
        .cp-title { font-size:22px; font-weight:700; margin-top:4px; }
        .cp-header-right { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
        .cp-admin-pill {
          display:inline-flex; align-items:center; gap:7px; padding:6px 12px;
          border-radius:999px; font-size:12px; font-weight:700; color:#fff;
          background:${C.accent}; box-shadow:0 8px 20px -10px ${C.accent}99;
        }

        .cp-toolbar { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:12px; margin-bottom:16px; }
        .cp-chip-row { display:flex; flex-wrap:wrap; gap:8px; }
        .cp-tools { display:flex; flex-wrap:wrap; align-items:center; gap:10px; }

        .cp-search { position:relative; }
        .cp-search input { width:230px; padding-left:34px; }
        .cp-search-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:var(--text-3); font-size:12.5px; }

        .cp-select-wrap { position:relative; }
        .cp-select-wrap select { width:152px; padding-right:30px; }
        .cp-select-icon { position:absolute; right:10px; top:50%; transform:translateY(-50%); color:var(--text-3); font-size:11px; pointer-events:none; }

        .cp-panel { padding:0; overflow:hidden; }
        .cp-table-wrap { overflow-x:auto; }
        .cp-admin-cell { background:rgba(124,58,237,.05); border-left:1px solid var(--border); }

        .cp-dd { position:relative; }
        .cp-dd-menu { position:absolute; right:0; top:calc(100% + 6px); width:192px; padding:6px; z-index:40; }
        .cp-dd-item {
          width:100%; display:flex; align-items:center; gap:9px; text-align:left;
          background:transparent; border:none; color:var(--text-2); font-size:12.5px;
          font-weight:600; padding:8px 10px; border-radius:8px; cursor:pointer;
        }
        .cp-dd-item:hover { background:var(--surface-2); color:var(--text-1); }
        .cp-dd-item.active { color:var(--accent); }
        .cp-dd-icon { display:inline-flex; width:14px; justify-content:center; }

        .cp-cards { display:flex; flex-direction:column; gap:12px; padding:14px; }
        .cp-mobile-card { padding:14px; display:flex; flex-direction:column; gap:10px; }
        .cp-mobile-top { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
        .cp-mobile-meta { display:flex; flex-wrap:wrap; gap:10px; font-size:11.5px; color:var(--text-3); }
        .cp-mobile-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

        @media (max-width: 640px) {
          .cp-toolbar { flex-direction:column; align-items:stretch; }
          .cp-tools { flex-direction:column; align-items:stretch; }
          .cp-search input, .cp-select-wrap, .cp-select-wrap select { width:100%; }
          .cp-tools .btn { justify-content:center; }
        }
      `}</style>
    </div>
  );
}
